/**
 * POS Demo Page
 * =============
 * 
 * Interactive POS demo for the homepage.
 * Uses isolated demo data - no real payments or emails.
 * Access logic:
 * - Anonymous: Full demo + sign-up CTA
 * - Logged-in (no POS): Full demo + upgrade CTA
 * - POS Merchant: "Go to My POS" redirect
 */

import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  CreditCard,
  Banknote,
  User,
  Tag,
  Receipt,
  X,
  Check,
  Package,
  PoundSterling,
  ArrowLeft,
  ArrowRight,
  LayoutGrid,
  Star,
  Sparkles,
  Home,
  Gift,
  Brain,
  ChevronRight,
  AlertCircle,
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const PosDemoPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is a POS merchant
  const isPOSMerchant = user?.role === "merchant" && user?.is_approved;
  
  // Redirect POS merchants to their real dashboard
  useEffect(() => {
    if (isPOSMerchant) {
      navigate("/merchant/pos");
    }
  }, [isPOSMerchant, navigate]);
  
  // Demo data state
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Cart state
  const [cart, setCart] = useState([]);
  
  // Customer state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  
  // Loyalty state
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  
  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [cashTendered, setCashTendered] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Receipt modal
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [completedTransaction, setCompletedTransaction] = useState(null);
  
  // Discount state
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState("");
  const [currentDiscount, setCurrentDiscount] = useState(null);
  
  // AI Assistant state
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiResponse, setAiResponse] = useState(null);
  const [askingAI, setAskingAI] = useState(false);
  
  // Fetch demo products
  const fetchProducts = async () => {
    try {
      let url = `${API}/pos/demo/products?limit=50`;
      if (selectedCategory && selectedCategory !== "all") {
        url += `&category_id=${selectedCategory}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (error) {
      console.error("Error fetching demo products:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch demo categories
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/pos/demo/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching demo categories:", error);
    }
  };
  
  // Fetch loyalty config
  const fetchLoyaltyConfig = async () => {
    try {
      const res = await fetch(`${API}/pos/demo/loyalty-config`);
      if (res.ok) {
        const data = await res.json();
        setLoyaltyConfig(data);
      }
    } catch (error) {
      console.error("Error fetching loyalty config:", error);
    }
  };
  
  // Fetch AI suggestions
  const fetchAISuggestions = async () => {
    try {
      const res = await fetch(`${API}/pos/demo/ai/suggestions`);
      if (res.ok) {
        const data = await res.json();
        setAiSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching AI suggestions:", error);
    }
  };
  
  useEffect(() => {
    fetchCategories();
    fetchLoyaltyConfig();
    fetchAISuggestions();
  }, []);
  
  useEffect(() => {
    fetchProducts();
  }, [searchQuery, selectedCategory]);
  
  // Search customers
  const searchCustomers = async (query) => {
    if (query.length < 2) {
      setCustomerResults([]);
      return;
    }
    
    setSearchingCustomers(true);
    try {
      const res = await fetch(`${API}/pos/demo/customers/search?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setCustomerResults(data);
      }
    } catch (err) {
      console.error("Error searching customers:", err);
    } finally {
      setSearchingCustomers(false);
    }
  };
  
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      }
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [customerSearch]);
  
  // Select a customer
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch("");
    setCustomerResults([]);
    setShowCustomerSearch(false);
    setPointsToRedeem(0);
    setLoyaltyDiscount(0);
    toast.success(`Selected ${customer.name}`);
  };
  
  // Clear selected customer
  const clearCustomer = () => {
    setSelectedCustomer(null);
    setPointsToRedeem(0);
    setLoyaltyDiscount(0);
  };
  
  // Handle loyalty points redemption
  const handleRedeemPoints = (points) => {
    if (!loyaltyConfig || !selectedCustomer) return;
    
    const subtotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const maxRedeemable = Math.min(
      selectedCustomer.loyalty_points,
      Math.floor(subtotal / loyaltyConfig.redemption_value) * loyaltyConfig.redemption_rate
    );
    
    const actualPoints = Math.min(points, maxRedeemable);
    const redemptionUnits = Math.floor(actualPoints / loyaltyConfig.redemption_rate);
    const discount = redemptionUnits * loyaltyConfig.redemption_value;
    
    setPointsToRedeem(redemptionUnits * loyaltyConfig.redemption_rate);
    setLoyaltyDiscount(discount);
  };
  
  // Calculate totals
  const calculateTotals = useCallback(() => {
    const subtotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    
    let orderDiscount = 0;
    if (currentDiscount) {
      if (currentDiscount.type === "percentage") {
        orderDiscount = Math.round(subtotal * currentDiscount.value / 100);
      } else {
        orderDiscount = Math.round(currentDiscount.value * 100);
      }
    }
    
    const totalDiscount = orderDiscount + loyaltyDiscount;
    const taxRate = 20;
    const taxableAmount = subtotal - totalDiscount;
    const taxAmount = Math.round(taxableAmount * taxRate / (100 + taxRate));
    const total = subtotal - totalDiscount;
    
    return {
      subtotal,
      discount: orderDiscount,
      loyaltyDiscount,
      tax: taxAmount,
      total,
      itemsCount: cart.length,
      unitsCount: cart.reduce((sum, item) => sum + item.quantity, 0)
    };
  }, [cart, currentDiscount, loyaltyDiscount]);
  
  const totals = calculateTotals();
  
  const pointsToEarn = loyaltyConfig && totals.total > 0
    ? Math.floor((totals.total / 100) * loyaltyConfig.points_per_pound)
    : 0;
  
  // Add to cart
  const addToCart = (product) => {
    const existingIndex = cart.findIndex(item => item.product_id === product.product_id);
    
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...cart, {
        item_id: `demo_${Date.now()}`,
        product_id: product.product_id,
        sku: product.sku,
        name: product.name,
        quantity: 1,
        unit_price: product.retail_price,
        cost_price: product.cost_price
      }]);
    }
    
    toast.success(`Added ${product.name}`);
  };
  
  // Update cart item quantity
  const updateQuantity = (itemId, delta) => {
    const newCart = cart.map(item => {
      if (item.item_id === itemId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean);
    
    setCart(newCart);
  };
  
  // Remove from cart
  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.item_id !== itemId));
  };
  
  // Clear cart
  const clearCart = () => {
    setCart([]);
    setCurrentDiscount(null);
    setSelectedCustomer(null);
    setPointsToRedeem(0);
    setLoyaltyDiscount(0);
  };
  
  // Apply discount
  const applyDiscount = () => {
    if (!discountValue) {
      toast.error("Please enter a discount value");
      return;
    }
    
    setCurrentDiscount({
      type: discountType,
      value: parseFloat(discountValue)
    });
    
    setShowDiscountModal(false);
    setDiscountValue("");
    toast.success("Discount applied");
  };
  
  // Process demo checkout
  const processCheckout = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      const res = await fetch(`${API}/pos/demo/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: cart,
          customer_id: selectedCustomer?.customer_id,
          payment_method: paymentMethod,
          discount_type: currentDiscount?.type,
          discount_value: currentDiscount?.value,
          loyalty_points_redeem: pointsToRedeem
        })
      });
      
      if (res.ok) {
        const txn = await res.json();
        setCompletedTransaction(txn);
        setShowPaymentModal(false);
        setShowReceiptModal(true);
        clearCart();
        toast.success("Demo transaction completed!");
      } else {
        toast.error("Transaction failed");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Transaction failed");
    } finally {
      setProcessingPayment(false);
    }
  };
  
  // Ask AI
  const askAI = async (question) => {
    setAskingAI(true);
    setAiResponse(null);
    
    try {
      const res = await fetch(`${API}/pos/demo/ai/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAiResponse(data.response);
      }
    } catch (error) {
      console.error("AI error:", error);
      setAiResponse("Sorry, I couldn't process that request. Try again!");
    } finally {
      setAskingAI(false);
    }
  };
  
  // Format price
  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
  
  // Get CTA based on user state
  const getCTA = () => {
    if (!user) {
      return {
        text: "Sign Up for Full Access",
        href: "/register",
        variant: "default"
      };
    } else if (user.role === "customer") {
      return {
        text: "Upgrade to Merchant + POS",
        href: "/pricing",
        variant: "default"
      };
    } else if (user.role === "merchant" && !user.is_approved) {
      return {
        text: "Complete Your Setup",
        href: "/merchant",
        variant: "outline"
      };
    }
    return null;
  };
  
  const cta = getCTA();

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-dark border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="h-5 w-5 text-slate-400" />
              <img src={COMPANY_LOGO} alt="VEYRONIX" className="h-8 w-auto brightness-0 invert" />
            </Link>
            <Badge variant="outline" className="border-indigo-500/50 text-indigo-400 bg-indigo-500/10">
              Interactive Demo
            </Badge>
          </div>
          
          <div className="flex items-center gap-3">
            {/* AI Assistant Button */}
            <Button
              variant="outline"
              size="sm"
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              onClick={() => setShowAIModal(true)}
              data-testid="demo-ai-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              AI Assistant
            </Button>
            
            {cta && (
              <Link to={cta.href}>
                <Button
                  size="sm"
                  className={cta.variant === "default" 
                    ? "bg-indigo-600 hover:bg-indigo-700" 
                    : "border-white/20 text-white"}
                  variant={cta.variant}
                  data-testid="demo-cta-btn"
                >
                  {cta.text}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Demo Banner */}
      <div className="fixed top-[57px] left-0 right-0 z-40 bg-gradient-to-r from-amber-500/90 to-orange-500/90 py-2 text-center">
        <p className="text-white text-sm font-medium flex items-center justify-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <span>This is a <strong>demo</strong> - no real payments or data changes</span>
        </p>
      </div>

      {/* Main Content */}
      <div className="pt-28 pb-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Feature Highlights */}
          <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: ShoppingCart, label: "Quick Checkout", color: "text-blue-400" },
              { icon: User, label: "Customer Lookup", color: "text-green-400" },
              { icon: Star, label: "Loyalty Points", color: "text-amber-400" },
              { icon: Brain, label: "AI Insights", color: "text-purple-400" }
            ].map((feature, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900/50 border border-white/5"
              >
                <feature.icon className={`h-4 w-4 ${feature.color}`} />
                <span className="text-sm text-slate-300">{feature.label}</span>
              </div>
            ))}
          </div>
          
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Products Panel */}
            <div className="lg:col-span-2 space-y-4">
              {/* Search & Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search products or scan barcode..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-900 border-white/10 text-white"
                    data-testid="demo-search-input"
                  />
                </div>
              </div>
              
              {/* Categories */}
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                <Button
                  variant={selectedCategory === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory("all")}
                  className={selectedCategory === "all" 
                    ? "bg-indigo-600 hover:bg-indigo-700" 
                    : "border-white/10 text-slate-300 hover:text-white"}
                  data-testid="demo-cat-all"
                >
                  <LayoutGrid className="h-4 w-4 mr-1" />
                  All
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat.category_id}
                    variant={selectedCategory === cat.category_id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(cat.category_id)}
                    className={selectedCategory === cat.category_id
                      ? "bg-indigo-600 hover:bg-indigo-700"
                      : "border-white/10 text-slate-300 hover:text-white"}
                    data-testid={`demo-cat-${cat.category_id}`}
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>
              
              {/* Products Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="aspect-square bg-slate-800 rounded-xl animate-pulse" />
                  ))
                ) : products.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-slate-400">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No products found</p>
                  </div>
                ) : (
                  products.map((product) => (
                    <button
                      key={product.product_id}
                      onClick={() => addToCart(product)}
                      className="group p-3 rounded-xl bg-slate-900/50 border border-white/10 hover:border-indigo-500/50 transition-all text-left"
                      data-testid={`demo-product-${product.product_id}`}
                    >
                      <div className="aspect-square bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg mb-2 flex items-center justify-center">
                        <Package className="h-8 w-8 text-slate-600 group-hover:text-indigo-400 transition-colors" />
                      </div>
                      <h3 className="font-medium text-sm text-white truncate">{product.name}</h3>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-indigo-400 font-bold">{formatPrice(product.retail_price)}</span>
                        <span className="text-xs text-slate-500">{product.total_stock} in stock</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
            
            {/* Cart Panel */}
            <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-4 h-fit sticky top-32">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-lg flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-indigo-400" />
                  Cart
                </h2>
                {cart.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearCart}
                    className="text-slate-400 hover:text-red-400"
                    data-testid="demo-clear-cart"
                  >
                    Clear
                  </Button>
                )}
              </div>
              
              {/* Customer Selection */}
              <div className="mb-4 p-3 rounded-lg bg-slate-800/50 border border-white/5">
                {selectedCustomer ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                        <User className="h-4 w-4 text-indigo-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{selectedCustomer.name}</p>
                        <p className="text-xs text-amber-400 flex items-center gap-1">
                          <Star className="h-3 w-3" />
                          {selectedCustomer.loyalty_points} points
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearCustomer}
                      className="text-slate-400 hover:text-white h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-slate-400 hover:text-white"
                    onClick={() => setShowCustomerSearch(true)}
                    data-testid="demo-add-customer"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Add Customer for Loyalty
                  </Button>
                )}
              </div>
              
              {/* Cart Items */}
              <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
                {cart.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Add products to cart</p>
                  </div>
                ) : (
                  cart.map((item) => (
                    <div
                      key={item.item_id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/30"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <p className="text-xs text-slate-400">{formatPrice(item.unit_price)}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                          onClick={() => updateQuantity(item.item_id, -1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                          onClick={() => updateQuantity(item.item_id, 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-slate-400 hover:text-red-400 ml-1"
                          onClick={() => removeFromCart(item.item_id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Discount & Loyalty */}
              {cart.length > 0 && (
                <div className="space-y-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-white/10 text-slate-300"
                    onClick={() => setShowDiscountModal(true)}
                    data-testid="demo-add-discount"
                  >
                    <Tag className="h-4 w-4 mr-2" />
                    {currentDiscount 
                      ? `Discount: ${currentDiscount.type === "percentage" ? `${currentDiscount.value}%` : formatPrice(currentDiscount.value * 100)}`
                      : "Add Discount"}
                  </Button>
                  
                  {selectedCustomer && selectedCustomer.loyalty_points > 0 && (
                    <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <p className="text-xs text-amber-400 mb-2">Redeem Loyalty Points</p>
                      <div className="flex gap-1">
                        {[0, 100, 200, selectedCustomer.loyalty_points].map((pts, idx) => (
                          <Button
                            key={idx}
                            variant={pointsToRedeem === pts ? "default" : "outline"}
                            size="sm"
                            className={`flex-1 text-xs ${pointsToRedeem === pts ? "bg-amber-500 hover:bg-amber-600" : "border-amber-500/30 text-amber-400"}`}
                            onClick={() => handleRedeemPoints(pts)}
                          >
                            {pts === 0 ? "None" : pts === selectedCustomer.loyalty_points ? "All" : pts}
                          </Button>
                        ))}
                      </div>
                      {loyaltyDiscount > 0 && (
                        <p className="text-xs text-emerald-400 mt-2">
                          Saving {formatPrice(loyaltyDiscount)} with {pointsToRedeem} points
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Totals */}
              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-slate-400">
                  <span>Subtotal ({totals.unitsCount} items)</span>
                  <span>{formatPrice(totals.subtotal)}</span>
                </div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-sm text-emerald-400">
                    <span>Discount</span>
                    <span>-{formatPrice(totals.discount)}</span>
                  </div>
                )}
                {totals.loyaltyDiscount > 0 && (
                  <div className="flex justify-between text-sm text-amber-400">
                    <span>Loyalty Points</span>
                    <span>-{formatPrice(totals.loyaltyDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-slate-400">
                  <span>VAT (20%)</span>
                  <span>{formatPrice(totals.tax)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/10">
                  <span>Total</span>
                  <span className="text-indigo-400">{formatPrice(totals.total)}</span>
                </div>
                
                {selectedCustomer && pointsToEarn > 0 && (
                  <p className="text-xs text-center text-amber-400">
                    +{pointsToEarn} loyalty points will be earned
                  </p>
                )}
              </div>
              
              {/* Checkout Button */}
              <Button
                className="w-full mt-4 h-12 bg-indigo-600 hover:bg-indigo-700 text-lg font-semibold"
                disabled={cart.length === 0}
                onClick={() => setShowPaymentModal(true)}
                data-testid="demo-checkout-btn"
              >
                <CreditCard className="h-5 w-5 mr-2" />
                Pay {formatPrice(totals.total)}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Search Modal */}
      <Dialog open={showCustomerSearch} onOpenChange={setShowCustomerSearch}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Find Customer</DialogTitle>
            <DialogDescription className="text-slate-400">
              Search by name, email, or phone
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search customers..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="pl-10 bg-slate-800 border-white/10"
                autoFocus
                data-testid="demo-customer-search-input"
              />
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {searchingCustomers ? (
                <div className="text-center py-4 text-slate-400">Searching...</div>
              ) : customerResults.length === 0 && customerSearch.length >= 2 ? (
                <div className="text-center py-4 text-slate-400">No customers found</div>
              ) : (
                customerResults.map((customer) => (
                  <button
                    key={customer.customer_id}
                    onClick={() => selectCustomer(customer)}
                    className="w-full p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 text-left transition-colors"
                    data-testid={`demo-customer-${customer.customer_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{customer.name}</p>
                        <p className="text-sm text-slate-400">{customer.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-400 text-sm flex items-center gap-1">
                          <Star className="h-3 w-3" />
                          {customer.loyalty_points} pts
                        </p>
                        <p className="text-xs text-slate-500">{customer.total_orders} orders</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discount Modal */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Apply Discount</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a percentage or fixed amount discount
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant={discountType === "percentage" ? "default" : "outline"}
                onClick={() => setDiscountType("percentage")}
                className={discountType === "percentage" ? "bg-indigo-600" : "border-white/10"}
              >
                Percentage %
              </Button>
              <Button
                variant={discountType === "fixed" ? "default" : "outline"}
                onClick={() => setDiscountType("fixed")}
                className={discountType === "fixed" ? "bg-indigo-600" : "border-white/10"}
              >
                Fixed Amount
              </Button>
            </div>
            <Input
              type="number"
              placeholder={discountType === "percentage" ? "Enter %" : "Enter amount"}
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              className="bg-slate-800 border-white/10"
              data-testid="demo-discount-value"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountModal(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={applyDiscount} className="bg-indigo-600 hover:bg-indigo-700">
              Apply Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Payment Method</DialogTitle>
            <DialogDescription className="text-slate-400">
              Select how you'd like to pay (demo only)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setPaymentMethod("card")}
                className={`p-4 rounded-xl border-2 transition-all ${
                  paymentMethod === "card"
                    ? "border-indigo-500 bg-indigo-500/10"
                    : "border-white/10 hover:border-white/20"
                }`}
                data-testid="demo-pay-card"
              >
                <CreditCard className={`h-8 w-8 mx-auto mb-2 ${paymentMethod === "card" ? "text-indigo-400" : "text-slate-400"}`} />
                <p className="font-medium">Card</p>
              </button>
              <button
                onClick={() => setPaymentMethod("cash")}
                className={`p-4 rounded-xl border-2 transition-all ${
                  paymentMethod === "cash"
                    ? "border-emerald-500 bg-emerald-500/10"
                    : "border-white/10 hover:border-white/20"
                }`}
                data-testid="demo-pay-cash"
              >
                <Banknote className={`h-8 w-8 mx-auto mb-2 ${paymentMethod === "cash" ? "text-emerald-400" : "text-slate-400"}`} />
                <p className="font-medium">Cash</p>
              </button>
            </div>
            
            {paymentMethod === "cash" && (
              <div>
                <label className="text-sm text-slate-400 block mb-2">Cash Tendered</label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={cashTendered}
                  onChange={(e) => setCashTendered(e.target.value)}
                  className="bg-slate-800 border-white/10"
                  data-testid="demo-cash-tendered"
                />
                {cashTendered && parseFloat(cashTendered) * 100 >= totals.total && (
                  <p className="text-emerald-400 text-sm mt-2">
                    Change: {formatPrice(parseFloat(cashTendered) * 100 - totals.total)}
                  </p>
                )}
              </div>
            )}
            
            <div className="p-4 rounded-lg bg-slate-800/50 border border-white/5">
              <div className="flex justify-between text-lg font-bold">
                <span>Total to Pay</span>
                <span className="text-indigo-400">{formatPrice(totals.total)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)} className="border-white/10">
              Cancel
            </Button>
            <Button 
              onClick={processCheckout} 
              disabled={processingPayment || (paymentMethod === "cash" && (!cashTendered || parseFloat(cashTendered) * 100 < totals.total))}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="demo-confirm-payment"
            >
              {processingPayment ? "Processing..." : "Complete Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                <Check className="h-8 w-8 text-emerald-500" />
              </div>
              Demo Transaction Complete!
            </DialogTitle>
            <DialogDescription className="text-center text-slate-400">
              This was a simulated transaction
            </DialogDescription>
          </DialogHeader>
          
          {completedTransaction && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-slate-800/50 border border-white/5">
                <p className="text-center text-sm text-slate-400 mb-2">Transaction</p>
                <p className="text-center font-mono font-bold">{completedTransaction.transaction_number}</p>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Items</span>
                  <span>{completedTransaction.items?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Subtotal</span>
                  <span>{formatPrice(completedTransaction.totals?.subtotal || 0)}</span>
                </div>
                {completedTransaction.totals?.discount > 0 && (
                  <div className="flex justify-between text-emerald-400">
                    <span>Discount</span>
                    <span>-{formatPrice(completedTransaction.totals.discount)}</span>
                  </div>
                )}
                {completedTransaction.totals?.loyalty_discount > 0 && (
                  <div className="flex justify-between text-amber-400">
                    <span>Loyalty Points</span>
                    <span>-{formatPrice(completedTransaction.totals.loyalty_discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/10">
                  <span>Total Paid</span>
                  <span className="text-indigo-400">{formatPrice(completedTransaction.totals?.total || 0)}</span>
                </div>
              </div>
              
              {completedTransaction.loyalty?.points_earned > 0 && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
                  <Star className="h-5 w-5 text-amber-400 mx-auto mb-1" />
                  <p className="text-amber-400 font-medium">+{completedTransaction.loyalty.points_earned} points earned!</p>
                </div>
              )}
              
              <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-center">
                <p className="text-indigo-400 text-sm">
                  Want the real POS experience? Sign up to process actual payments!
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-col gap-2">
            <Button 
              onClick={() => setShowReceiptModal(false)} 
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              New Transaction
            </Button>
            {cta && (
              <Link to={cta.href} className="w-full">
                <Button variant="outline" className="w-full border-white/10">
                  {cta.text}
                </Button>
              </Link>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Assistant Modal */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-400" />
              AI Retail Assistant
              <Badge variant="outline" className="ml-2 border-purple-500/50 text-purple-400 text-xs">Demo</Badge>
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Ask questions about your sales, inventory, and customers
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Suggestion Cards */}
            <div className="grid grid-cols-2 gap-2">
              {aiSuggestions.slice(0, 4).map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => {
                    setAiQuestion(suggestion.question);
                    askAI(suggestion.question);
                  }}
                  className="p-3 rounded-lg bg-slate-800/50 border border-white/5 hover:border-purple-500/30 text-left transition-all"
                  data-testid={`demo-ai-suggestion-${suggestion.id}`}
                >
                  <p className="text-sm text-slate-300">{suggestion.question}</p>
                </button>
              ))}
            </div>
            
            {/* Question Input */}
            <div className="flex gap-2">
              <Input
                placeholder="Ask me anything..."
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && aiQuestion && askAI(aiQuestion)}
                className="bg-slate-800 border-white/10"
                data-testid="demo-ai-input"
              />
              <Button 
                onClick={() => aiQuestion && askAI(aiQuestion)}
                disabled={!aiQuestion || askingAI}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="demo-ai-ask-btn"
              >
                {askingAI ? "..." : "Ask"}
              </Button>
            </div>
            
            {/* AI Response */}
            {aiResponse && (
              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Brain className="h-4 w-4 text-purple-400" />
                  </div>
                  <p className="text-sm text-slate-300">{aiResponse}</p>
                </div>
              </div>
            )}
            
            <div className="p-3 rounded-lg bg-slate-800/50 text-center">
              <p className="text-xs text-slate-500">
                Full AI assistant available with Veyronix POS subscription
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PosDemoPage;
